import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight,
  FiAward,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiShield,
  FiTruck,
} from 'react-icons/fi';

import useCarousel from '../Admin/useCarousel';
import useCategories from '../Admin/useCategories';
import HotProducts from '../Content/HotProducts';
import NewArrival from '../Content/NewArrival';
import AlsoLike from '../Content/AlsoLike';
import BestReviews from '../Content/BestReviews';
import { InlineLoader } from '../common/States';
import SmartImage from '../common/SmartImage';
import Reveal from '../common/Reveal';
import { apiUrl, assetUrl } from '../../config/api';
import './Landingpage.scss';

const GUIDES = [
  { key: 'her', to: '/product?guide=her', label: 'For Her' },
  { key: 'mom', to: '/product?guide=mom', label: 'For Mom' },
  { key: 'friend', to: '/product?guide=friend', label: 'For a Friend' },
  { key: 'sister', to: '/product?guide=sister', label: 'For Sister' },
];

const PRICE_TIERS = [
  { key: 'five', to: '/product?price_min=0&price_max=15', label: 'Under $15' },
  { key: 'fifteen', to: '/product?price_min=0&price_max=20', label: 'Under $20' },
  { key: 'twenty', to: '/product?price_min=0&price_max=25', label: 'Under $25' },
  { key: 'thirty', to: '/product?price_min=30&price_max=1000', label: '$30 and above' },
];

/* Service promises under the hero. Each one links to the page that backs it
   up, so the strip is a shortcut rather than decoration. */
const PROMISES = [
  {
    key: 'shipping',
    to: '/policies?id=2',
    Icon: FiTruck,
    title: 'Shipped across the USA',
    text: 'Tracked delivery on every order',
  },
  {
    key: 'craft',
    to: '/aboutus',
    Icon: FiAward,
    title: 'Handcrafted pieces',
    text: 'Made by Indian artisans',
  },
  {
    key: 'payment',
    to: '/policies?id=1',
    Icon: FiShield,
    title: 'Secure checkout',
    text: 'PayPal, Razorpay and cards',
  },
  {
    key: 'returns',
    to: '/policies?id=3',
    Icon: FiRefreshCw,
    title: 'Easy exchanges',
    text: 'Straightforward returns policy',
  },
];

/** Heading block shared by every landing section. */
const SectionHead = ({ id, eyebrow, title, subtitle, action }) => (
  <Reveal className="ij-sec-head">
    <div className="ij-sec-head__text">
      {eyebrow && <span className="ij-sec-head__eyebrow">{eyebrow}</span>}
      <h2 id={id} className="ij-sec-head__title">
        {title}
      </h2>
      {subtitle && <p className="ij-sec-head__subtitle">{subtitle}</p>}
    </div>
    {action}
  </Reveal>
);

/** Small "see everything" link with an arrow that slides on hover. */
const MoreLink = ({ to, children }) => (
  <Link to={to} className="ij-more">
    {children}
    <FiArrowRight aria-hidden="true" />
  </Link>
);

/**
 * Banner carousel.
 *
 * Slides are rendered at their own natural aspect ratio — the admin uploads
 * everything from 3:2 key visuals to ~15:2 strip banners, and a fixed
 * `aspect-ratio` + `object-fit: cover` cropped both (the strip banner lost
 * most of its width). `variant` only decides how much space the skeleton
 * reserves before the image arrives.
 *
 * Slides cross-fade rather than slide: the artwork varies in shape, and a
 * fade never moves a banner sideways under the reader. The entry animation
 * settles from a 1.03 scale back to exactly 1, so the authored framing is
 * what stays on screen once the motion stops.
 */
const HeroCarousel = ({ slides, loading, variant = 'hero', priority = false }) => {
  if (loading) {
    return (
      <div
        className={`ij-hero__skeleton ij-hero__skeleton--${variant} ij-skeleton`}
        aria-hidden="true"
      />
    );
  }
  if (!slides.length) return null;

  const multiple = slides.length > 1;

  return (
    <Carousel
      className={`ij-hero ij-hero--${variant}`}
      fade
      indicators={multiple}
      controls={multiple}
      interval={6000}
      pause="hover"
      touch
      prevLabel="Previous slide"
      nextLabel="Next slide"
      prevIcon={
        <span className="ij-hero__nav" aria-hidden="true">
          <FiChevronLeft />
        </span>
      }
      nextIcon={
        <span className="ij-hero__nav" aria-hidden="true">
          <FiChevronRight />
        </span>
      }
    >
      {slides.map((slide, index) => (
        <Carousel.Item key={slide.id ?? slide.image_path ?? index}>
          <img
            className="ij-hero__img"
            src={assetUrl(slide.image_path)}
            alt={slide.title || ''}
            /* The first hero image is the LCP element — never lazy-load it. */
            loading={priority && index === 0 ? 'eager' : 'lazy'}
            fetchpriority={priority && index === 0 ? 'high' : undefined}
            decoding="async"
          />
        </Carousel.Item>
      ))}
    </Carousel>
  );
};

const Landingpage = () => {
  const { categories, loading: categoriesLoading } = useCategories();
  const { firstCarousel, secondCarousel, loading: carouselLoading } =
    useCarousel();

  const [landing, setLanding] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(apiUrl('/api/fetch-landing'), {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        // `landing` used to be read straight off an object that started as
        // `{}`, so the page threw whenever this request had not resolved yet.
        if (isMounted.current) setLanding(data?.landing || {});
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted.current) setLanding({});
      }
    })();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const renderTile = useCallback(
    ({ key, to, label }, shape) => {
      const image = landing?.[key];
      if (!image) return null;

      return (
        <Link key={key} to={to} className={`ij-tile ij-tile--${shape}`}>
          <span className="ij-tile__media">
            <SmartImage src={assetUrl(image)} alt="" />
          </span>
          <span className="ij-tile__label">{label}</span>
          {shape === 'wide' && (
            <span className="ij-tile__cta" aria-hidden="true">
              Shop now
              <FiArrowRight />
            </span>
          )}
        </Link>
      );
    },
    [landing]
  );

  return (
    <>
      <Helmet>
        <title>Indian Jewelryish — Handcrafted Indian Jewellery</title>
      </Helmet>

      {/* The page needs a level-one heading for assistive tech and SEO;
          the hero artwork carries the visual branding. */}
      <h1 className="ij-visually-hidden">
        Indian Jewelryish — handcrafted Indian jewellery
      </h1>

      {/* ---------------- Hero ---------------- */}
      <section className="ij-stage" aria-label="Featured">
        <span className="ij-stage__glow ij-stage__glow--start" aria-hidden="true" />
        <span className="ij-stage__glow ij-stage__glow--end" aria-hidden="true" />

        <div className="ij-stage__inner">
          <HeroCarousel
            slides={firstCarousel}
            loading={carouselLoading}
            priority
          />
        </div>
      </section>

      {/* ---------------- Service promises ---------------- */}
      <section className="ij-promises" aria-label="Why shop with us">
        <Reveal className="ij-container ij-promises__grid" stagger>
          {PROMISES.map(({ key, to, Icon, title, text }) => (
            <Link key={key} to={to} className="ij-promise">
              <span className="ij-promise__icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="ij-promise__body">
                <span className="ij-promise__title">{title}</span>
                <span className="ij-promise__text">{text}</span>
              </span>
            </Link>
          ))}
        </Reveal>
      </section>

      {/* ---------------- Shop by category ---------------- */}
      <section className="ij-section ij-container" aria-labelledby="cat-heading">
        <SectionHead
          id="cat-heading"
          eyebrow="Collections"
          title="Shop by category"
          subtitle="Explore the collection by the pieces you love most"
          action={<MoreLink to="/all-categories">All categories</MoreLink>}
        />

        {categoriesLoading ? (
          <div className="ij-rail-wrap">
            <div className="ij-circle-rail" aria-hidden="true">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="ij-tile ij-tile--circle">
                  <span className="ij-tile__media ij-skeleton" />
                  <span
                    className="ij-skeleton ij-skeleton--text"
                    style={{ width: 70 }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="ij-rail-wrap">
            <Reveal className="ij-circle-rail" stagger>
              {categories.map((category) => (
                <Link
                  key={category.category_code}
                  to={`/category?category=${category.category_code}`}
                  className="ij-tile ij-tile--circle"
                >
                  <span className="ij-tile__media">
                    <SmartImage src={assetUrl(category.category_image)} alt="" />
                  </span>
                  <span className="ij-tile__label">{category.category_name}</span>
                </Link>
              ))}
            </Reveal>
          </div>
        )}
      </section>

      {/* ---------------- New arrivals / hot products ---------------- */}
      <section
        className="ij-section ij-section--alt"
        aria-labelledby="products-heading"
      >
        <div className="ij-container">
          <h2 id="products-heading" className="ij-visually-hidden">
            Featured products
          </h2>

          <Reveal className="ij-segment-wrap">
            <div
              className="ij-segment"
              data-active={activeTab}
              role="tablist"
              aria-label="Featured products"
            >
              <span className="ij-segment__thumb" aria-hidden="true" />
              <button
                type="button"
                role="tab"
                id="tab-new"
                aria-selected={activeTab === 'new'}
                aria-controls="panel-new"
                className={`ij-segment__tab${
                  activeTab === 'new' ? ' is-active' : ''
                }`}
                onClick={() => setActiveTab('new')}
              >
                New arrivals
              </button>
              <button
                type="button"
                role="tab"
                id="tab-hot"
                aria-selected={activeTab === 'hot'}
                aria-controls="panel-hot"
                className={`ij-segment__tab${
                  activeTab === 'hot' ? ' is-active' : ''
                }`}
                onClick={() => setActiveTab('hot')}
              >
                Hot products
              </button>
            </div>
          </Reveal>

          {/* Both panels stay mounted so switching tabs does not refetch. */}
          <div
            className="ij-panel-fade"
            role="tabpanel"
            id="panel-new"
            aria-labelledby="tab-new"
            hidden={activeTab !== 'new'}
          >
            <NewArrival limit={10} layout="featured" />
          </div>
          <div
            className="ij-panel-fade"
            role="tabpanel"
            id="panel-hot"
            aria-labelledby="tab-hot"
            hidden={activeTab !== 'hot'}
          >
            <HotProducts limit={10} layout="featured" />
          </div>

          <div className="ij-section__cta">
            <Link
              to={activeTab === 'new' ? '/new-products' : '/hot-products'}
              className="ij-btn ij-btn--outline"
            >
              {activeTab === 'new' ? 'View all new arrivals' : 'View all hot products'}
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- Shop by guide ---------------- */}
      {landing === null ? (
        <div className="ij-section">
          <InlineLoader label="Loading collections" />
        </div>
      ) : (
        <>
          {GUIDES.some((guide) => landing[guide.key]) && (
            <section
              className="ij-section ij-container"
              aria-labelledby="guide-heading"
            >
              <SectionHead
                id="guide-heading"
                eyebrow="Gift guide"
                title="Shop by guide"
                subtitle="Thoughtful gifts for the people who matter"
              />
              <div className="ij-rail-wrap">
                <Reveal className="ij-circle-rail ij-circle-rail--lg" stagger>
                  {GUIDES.map((guide) => renderTile(guide, 'circle'))}
                </Reveal>
              </div>
            </section>
          )}

          {secondCarousel.length > 0 && (
            <section className="ij-stage ij-stage--banner" aria-label="Promotion">
              <Reveal className="ij-stage__inner" variant="zoom">
                <HeroCarousel
                  slides={secondCarousel}
                  loading={false}
                  variant="strip"
                />
              </Reveal>
            </section>
          )}

          {PRICE_TIERS.some((tier) => landing[tier.key]) && (
            <section
              className="ij-section ij-container"
              aria-labelledby="price-heading"
            >
              <SectionHead
                id="price-heading"
                eyebrow="Budget picks"
                title="Shop by price"
                subtitle="Something lovely at every price point"
              />
              <Reveal className="ij-price-grid" stagger>
                {PRICE_TIERS.map((tier) => renderTile(tier, 'wide'))}
              </Reveal>
            </section>
          )}

          {(landing.gold || landing.silver) && (
            <section
              className="ij-section ij-section--alt"
              aria-label="Collections"
            >
              <div className="ij-container ij-stack">
                {landing.gold && (
                  <Reveal variant="left">
                    <Link to="/product?jewellery_type=gold" className="ij-feature">
                      <div className="ij-feature__media">
                        <SmartImage src={assetUrl(landing.gold)} alt="" />
                      </div>
                      <div className="ij-feature__body">
                        <span className="ij-feature__eyebrow">Gold</span>
                        <h3 className="ij-feature__title">Gold Collection</h3>
                        {landing.gold_text && <p>{landing.gold_text}</p>}
                        <span className="ij-feature__cta">
                          Explore gold
                          <FiArrowRight aria-hidden="true" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                )}

                {landing.silver && (
                  <Reveal variant="right">
                    <Link
                      to="/product?jewellery_type=silver"
                      className="ij-feature ij-feature--reverse"
                    >
                      <div className="ij-feature__media">
                        <SmartImage src={assetUrl(landing.silver)} alt="" />
                      </div>
                      <div className="ij-feature__body">
                        <span className="ij-feature__eyebrow">Silver</span>
                        <h3 className="ij-feature__title">Silver Collection</h3>
                        {landing.silver_text && <p>{landing.silver_text}</p>}
                        <span className="ij-feature__cta">
                          Explore silver
                          <FiArrowRight aria-hidden="true" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* ---------------- You may also like ---------------- */}
      <section className="ij-section ij-container" aria-labelledby="also-heading">
        <SectionHead
          id="also-heading"
          eyebrow="Handpicked"
          title="You may also like"
          subtitle="A few more pieces worth a second look"
        />
        <div className="ij-section__body">
          <AlsoLike limit={10} layout="featured" />
        </div>
      </section>

      {/* ---------------- Closing call to action ---------------- */}
      <section className="ij-section ij-container" aria-labelledby="cta-heading">
        <Reveal className="ij-cta" variant="zoom">
          <span className="ij-cta__glow" aria-hidden="true" />
          <h2 id="cta-heading" className="ij-cta__title">
            Find the piece that feels like you
          </h2>
          <p className="ij-cta__text">
            Bridal sets, kundan, polki and everyday pieces — handcrafted and
            shipped to your door.
          </p>
          <div className="ij-cta__actions">
            <Link to="/allproducts" className="ij-btn ij-btn--primary ij-btn--lg">
              Shop all products
            </Link>
            <Link to="/all-categories" className="ij-btn ij-btn--outline ij-btn--lg">
              Browse categories
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ---------------- Testimonials ---------------- */}
      <section
        className="ij-section ij-section--alt"
        aria-labelledby="reviews-heading"
      >
        <div className="ij-container">
          <SectionHead
            id="reviews-heading"
            eyebrow="Testimonials"
            title="What our customers say"
            subtitle="Real words from people wearing our jewellery"
            action={<MoreLink to="/all-best-reviews">All reviews</MoreLink>}
          />
          <div className="ij-section__body">
            <BestReviews layout="rail" />
          </div>
        </div>
      </section>
    </>
  );
};

export default Landingpage;
